import React, { useEffect, useState } from 'react';
import { Wallet, CalendarDays } from 'lucide-react';
import { STATUS, fmtDay } from '../porters/porterUi';
import { money } from '../staff/StaffProfile';

export const MyWages = ({ authFetch, refreshKey }) => {
  const [d, setD] = useState(null);
  useEffect(() => { authFetch('get', '/staff/me/wages').then(({ data }) => setD(data)).catch(() => {}); }, [authFetch, refreshKey]);
  if (!d) return null;
  return (
    <section className="mb-6 rounded-xl border border-slate-200 bg-white p-5" data-testid="my-wages">
      <h2 className="font-display text-lg font-extrabold text-[color:var(--mv-navy)] flex items-center gap-2"><Wallet className="w-5 h-5 text-[color:var(--mv-orange)]" />My Wages</h2>
      <div className="text-xs text-slate-500">Pay period {fmtDay(d.period.start)} – {fmtDay(d.period.end)} · next payment {fmtDay(d.period.next_payment_date)}</div>
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[['Daily wage', money(d.daily_rate), 'mw-rate'], ['Days worked (confirmed)', `${d.days_confirmed}`, 'mw-days'], ['Earned gross', money(d.earned_gross), 'mw-earned'], ['Expected (scheduled)', money(d.forecast_gross), 'mw-forecast']].map(([l, v, t]) => (
          <div key={t} className="rounded-lg bg-slate-50 border border-slate-200 p-3"><div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{l}</div><div className="font-display text-xl font-black text-[color:var(--mv-navy)]" data-testid={t}>{v}</div></div>
        ))}
      </div>
      {d.adjustments.filter(a => a.status === 'approved').length > 0 && (
        <ul className="mt-2 text-xs text-slate-600" data-testid="mw-adjustments">{d.adjustments.filter(a => a.status === 'approved').map(a => <li key={a.id}>{a.kind}: {money(a.amount)} — {a.note}</li>)}</ul>
      )}
      {d.profit_share != null && <div className="mt-2 text-sm" data-testid="mw-profit-share">Profit share <strong>{money(d.profit_share)}</strong></div>}
      <div className="mt-2 text-sm">Total gross due <strong data-testid="mw-total">{money(d.total_due)}</strong> · <span className="capitalize" data-testid="mw-status">{d.payment_status}</span></div>
      <p className="text-[11px] text-slate-400 mt-1">Gross earnings estimate before tax/NI and other deductions — not a payslip. Earned wages only update once the office confirms the day.</p>
      {d.history.length > 0 && <ul className="mt-2 text-xs text-slate-600" data-testid="mw-history">{d.history.map(h => <li key={h.period_start}>{fmtDay(h.period_start)} period · {h.days} days · {money(h.gross)} · {h.status}</li>)}</ul>}
    </section>
  );
};

export const MySchedule = ({ authFetch, refreshKey }) => {
  const [d, setD] = useState(null);
  const [all, setAll] = useState(false);
  useEffect(() => { authFetch('get', '/staff/me/schedule?days=31').then(({ data }) => setD(data)).catch(() => {}); }, [authFetch, refreshKey]);
  if (!d) return null;
  const days = all ? d.days : d.days.slice(0, 7);
  return (
    <section className="mb-6 rounded-xl border border-slate-200 bg-white p-5" data-testid="my-schedule">
      <h2 className="font-display text-lg font-extrabold text-[color:var(--mv-navy)] flex items-center gap-2"><CalendarDays className="w-5 h-5 text-[color:var(--mv-orange)]" />My Schedule</h2>
      {d.preferred_hours && <div className="text-xs text-slate-500">Preferred hours: {d.preferred_hours}</div>}
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-1.5">
        {days.map((x, i) => {
          const st = STATUS[x.state] || STATUS.available;
          return (
            <div key={x.date} className={`rounded-md border p-2 text-xs ${i === 0 ? 'border-[color:var(--mv-orange)]' : 'border-slate-200'}`} data-testid={`ms-day-${x.date}`}>
              <div className="font-black text-[color:var(--mv-navy)]">{i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : fmtDay(x.date)}</div>
              <span className={`inline-flex items-center gap-1 px-1 rounded ${st.chip}`}><span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />{x.state === 'available' ? 'Working day' : st.label}</span>
              {x.jobs.map(j => <div key={j.quote_id} className="mt-0.5 font-bold">{j.time} · #{j.ref}</div>)}
            </div>
          );
        })}
      </div>
      <button type="button" onClick={() => setAll(v => !v)} className="mt-2 h-10 text-sm font-bold text-[color:var(--mv-orange)]" data-testid="ms-toggle">{all ? 'Show this week' : 'Show the month'}</button>
    </section>
  );
};
